
import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, GripVertical, Settings, Save, X, CheckCircle2, Loader2, List, MoreVertical, ArrowLeft } from 'lucide-react';
import { VacancyStage, AutomationFlow } from '../types';
import { supabase, supabase_get_flows } from '../services/supabaseService';
import ConfirmDialog from './ConfirmDialog';

interface StageConfiguratorProps {
    vacancyId: string;
    onUpdate: () => void;
}

const DEFAULT_STAGES: Partial<VacancyStage>[] = [
    { name: 'Shortlist', type: 'Screening', sortOrder: 0 },
    { name: 'Voorgesteld', type: 'Screening', sortOrder: 1 },
    { name: 'Gesprek', type: 'Interview', sortOrder: 2 },
    { name: 'Aanbod', type: 'Offer', sortOrder: 3 },
    { name: 'Plaatsing', type: 'Hired', sortOrder: 4 },
    { name: 'Afgewezen', type: 'Rejected', sortOrder: 5 }
];

const STAGE_TYPES = ['Screening', 'Interview', 'Offer', 'Hired', 'Rejected'];

const StageConfigurator: React.FC<StageConfiguratorProps> = ({ vacancyId, onUpdate }) => {
    const [stages, setStages] = useState<VacancyStage[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<VacancyStage>>({});

    // Automations State
    const [flows, setFlows] = useState<AutomationFlow[]>([]);

    // Error State
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    // Dialog State
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [stageToDelete, setStageToDelete] = useState<string | null>(null);

    useEffect(() => {
        if (vacancyId) fetchStages();
    }, [vacancyId]);

    const fetchStages = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const { data, error } = await supabase
                .from('vacancy_stages')
                .select('*')
                .eq('vacancy_id', vacancyId)
                .order('sort_order', { ascending: true });

            if (error) throw error;

            // Also fetch available flows
            try {
                const f = await supabase_get_flows();
                setFlows(f.filter((fc: any) => fc.status === 'active'));
            } catch (e) { }

            if (!data || data.length === 0) {
                console.log("No stages found for vacancy:", vacancyId, "Auto-initializing defaults...");
                await handleInitDefaults();
            } else {
                setStages(data as any);
                setLoading(false);
            }
        } catch (e: any) {
            console.error("Error fetching stages:", e);
            setErrorMsg(e.message || "Fout bij ophalen fases");
            setLoading(false);
        }
    };

    const handleInitDefaults = async () => {
        // setIsSaving(true); // unnecessary if called from fetchStages
        try {
            const toInsert = DEFAULT_STAGES.map((s, idx) => ({
                vacancy_id: vacancyId,
                name: s.name,
                type: s.type,
                sort_order: idx,
                requirements: { scorecard: false, interviewers: 1 }
            }));

            const { error } = await supabase.from('vacancy_stages').insert(toInsert);
            if (error) throw error;

            // Re-fetch to populate state
            const { data, error: fetchError } = await supabase
                .from('vacancy_stages')
                .select('*')
                .eq('vacancy_id', vacancyId)
                .order('sort_order', { ascending: true });

            if (fetchError) throw fetchError;
            if (data) setStages(data as any);
            onUpdate();
        } catch (e: any) {
            console.error("Init defaults failed:", e);
            setErrorMsg(`Initialisatie mislukt: ${e.message}`);
        } finally {
            // setIsSaving(false);
            setLoading(false); // Ensure loading is cleared
        }
    };

    const handleAddStage = async () => {
        const newStage = {
            vacancy_id: vacancyId,
            name: 'Nieuwe Fase',
            type: 'Interview',
            sort_order: stages.length,
            requirements: { scorecard: false, interviewers: 1 }
        };

        // Optimistic update
        const tempId = Math.random().toString();
        setStages([...stages, { ...newStage, id: tempId } as any]);

        try {
            const { data, error } = await supabase.from('vacancy_stages').insert(newStage).select();
            if (error) throw error;
            if (data) {
                // Replace temp with real
                const realStage = data[0];
                setStages(prev => prev.map(s => s.id === tempId ? realStage : s));
            }
        } catch (e) {
            console.error(e);
            fetchStages(); // Revert
        }
    };

    const handleMove = async (index: number, direction: 'up' | 'down') => {
        if ((direction === 'up' && index === 0) || (direction === 'down' && index === stages.length - 1)) return;

        const newStages = [...stages];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;

        // Swap
        [newStages[index], newStages[targetIndex]] = [newStages[targetIndex], newStages[index]];

        // Update sort orders locally
        newStages.forEach((s, i) => s.sortOrder = i);
        setStages(newStages);

        // Save order
        try {
            // In a real app, batch update. For now, loop (inefficient but works for small lists)
            for (let i = 0; i < newStages.length; i++) {
                await supabase.from('vacancy_stages').update({ sort_order: i }).eq('id', newStages[i].id);
            }
        } catch (e) {
            console.error("Failed to reorder", e);
        }
    };

    const handleEdit = (stage: VacancyStage) => {
        setEditingId(stage.id);
        setEditForm(stage);
    };

    const handleSaveEdit = async () => {
        if (!editingId || !editForm) return;

        // Optimistic
        setStages(stages.map(s => s.id === editingId ? { ...s, ...editForm } as VacancyStage : s));
        setEditingId(null);

        try {
            await supabase.from('vacancy_stages').update({
                name: editForm.name,
                type: editForm.type,
                requirements: editForm.requirements
            }).eq('id', editingId);
        } catch (e) {
            console.error(e);
            fetchStages();
        }
    };

    const confirmDelete = (id: string) => {
        setStageToDelete(id);
        setShowDeleteConfirm(true);
    };

    const handleDelete = async () => {
        if (!stageToDelete) return;

        const id = stageToDelete;
        setShowDeleteConfirm(false); // Close immediately for responsiveness
        setStageToDelete(null);

        setStages(stages.filter(s => s.id !== id));
        try {
            await supabase.from('vacancy_stages').delete().eq('id', id);
        } catch (e) {
            fetchStages();
        }
    };

    if (loading) return <div className="p-8 text-center"><Loader2 className="animate-spin mx-auto text-slate-300" /></div>;

    // Empty state is now auto-handled, but keep safe fallback just in case
    if (!loading && stages.length === 0) {
        return <div className="p-8 text-center text-slate-400">Stages laden...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto">
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                title="Fase Verwijderen"
                message="Weet je zeker dat je deze fase wilt verwijderen? Kandidaten in deze fase worden verplaatst naar 'Nieuw'."
                confirmLabel="Verwijderen"
                isDestructive
                onConfirm={handleDelete}
                onCancel={() => setShowDeleteConfirm(false)}
            />

            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <button onClick={onUpdate} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">Interview Flow</h3>
                        <p className="text-sm text-slate-500 font-medium mt-1">Slep en pas de stappen aan voor deze vacature.</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={handleAddStage}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-50 transition-all"
                    >
                        <Plus size={14} /> Stap
                    </button>
                    <button
                        onClick={onUpdate}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition-all"
                    >
                        <Save size={14} /> Opslaan
                    </button>
                </div>
            </div>

            <div className="space-y-3 bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm">
                {stages.map((stage, index) => (
                    <div
                        key={stage.id}
                        className={`
              flex items-center gap-4 p-4 rounded-xl border transition-all
              ${editingId === stage.id ? 'bg-red- border-red- ring-1 ring-red-' : 'bg-white border-slate-100 hover:border-slate-300'}
            `}
                    >
                        {/* Handle / Order */}
                        <div className="flex flex-col gap-1 text-slate-300">
                            <button
                                onClick={() => handleMove(index, 'up')}
                                disabled={index === 0}
                                className="hover:text-slate-600 disabled:opacity-30 p-0.5"
                            >
                                <ArrowUp size={14} />
                            </button>
                            <button
                                onClick={() => handleMove(index, 'down')}
                                disabled={index === stages.length - 1}
                                className="hover:text-slate-600 disabled:opacity-30 p-0.5"
                            >
                                <ArrowDown size={14} />
                            </button>
                        </div>

                        {editingId === stage.id ? (
                            <div className="flex-1 flex items-center gap-4 animate-fadeIn">
                                <div className="flex-1">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Naam</label>
                                    <input
                                        autoFocus
                                        type="text"
                                        value={editForm.name || ''}
                                        onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                        className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm font-bold shadow-sm outline-none focus:ring-2 focus:ring-red-"
                                    />
                                </div>
                                <div className="w-48">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 block mb-1">Type</label>
                                    <select
                                        value={editForm.type}
                                        onChange={e => setEditForm({ ...editForm, type: e.target.value as any })}
                                        className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm font-medium outline-none"
                                    >
                                        {STAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div className="flex items-center gap-2 mt-5">
                                    <button onClick={handleSaveEdit} className="p-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100"><Save size={18} /></button>
                                    <button onClick={() => setEditingId(null)} className="p-2 bg-slate-50 text-slate-400 rounded-lg hover:bg-slate-100"><X size={18} /></button>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-xs shrink-0
                  ${stage.type === 'Hired' ? 'bg-emerald-100 text-emerald-700' :
                                        stage.type === 'Rejected' ? 'bg-red-50 text-red-500' :
                                            stage.type === 'Offer' ? 'bg-amber-100 text-amber-700' :
                                                'bg-slate-100 text-slate-600'}
                `}>
                                    {index + 1}
                                </div>

                                <div className="flex-1">
                                    <h4 className="font-bold text-slate-900 text-sm">{stage.name}</h4>
                                    <div className="flex gap-3 text-[10px] uppercase font-bold text-slate-400 mt-1">
                                        <span>{stage.type}</span>
                                        {stage.requirements?.scorecard && <span className="text-red-0 flex items-center gap-1"><CheckCircle2 size={10} /> Scorecard</span>}
                                        {stage.requirements?.interviewers && stage.requirements.interviewers > 1 && <span className="text-purple-500 flex items-center gap-1"><CheckCircle2 size={10} /> {stage.requirements.interviewers} Interviewers</span>}
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <button onClick={() => handleEdit(stage)} className="p-2 text-slate-400 hover:text-red- hover:bg-red- rounded-lg transition-colors"><Settings size={16} /></button>
                                    <button onClick={() => confirmDelete(stage.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={16} /></button>
                                </div>
                            </>
                        )}
                    </div>
                ))}
            </div>

            <div className="mt-8 bg-slate-50 p-6 rounded-[2rem] border border-slate-200 text-center">
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-400 mb-2">Automatisering & Triggers</h4>
                <p className="text-sm text-slate-500 max-w-lg mx-auto mb-4">Je kunt per fase automatiseringen instellen, zoals het sturen van bevestigingen, reminders of assessments.</p>
                <button className="px-5 py-2.5 bg-white border border-slate-200 text-slate-700 font-bold text-xs rounded-xl hover:bg-slate-50 transition-all shadow-sm">
                    Naar Automation Manager
                </button>
            </div>
        </div>
    );
};

export default StageConfigurator;
